// Action Creators
// Action creator loader
import { BaseActionCreatorLoader } from "../../base/loaders/BaseActionCreatorLoader";
// Action creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { AppRootElementId, ClientType } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { LoggingStrings } from "@ms/uno-constants/lib/local/LoggingConstants";
// Controls
import { PlannerShell } from "@ms/uno-shell/lib/local/controls/plannerShell/PlannerShell";
// Host Adaptor
import { FailedReason } from "@ms/uno-hostadaptors/lib/local/IHostAdaptor";
import { MetaOSHostAdaptor } from "@ms/uno-hostadaptors/lib/local/metaOS/MetaOSHostAdaptor";
// Loaders
import { MetaOSAppLogicModuleLoader } from "../loaders/MetaOSAppLogicModuleLoader";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { WebPlatformPerformanceMarker } from "@ms/uno-telemetry/lib/local/performance/WebPlatformPerformanceMarker";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
// Utilities
import React from "react";
import { createRoot } from "react-dom/client";
import { initDataPreFetcher, triggerCaeGraphProbeAsync } from "../../utilities/AppBootstrapUtils";
import { configureHostAdaptor, setupProviders, setupWorkerLoaderAndRegisterTheme } from "../../utilities/MetaOSAppBootstrapSetupUtils";
/**
 * The production bootstrap function
 */
export async function bootstrap() {
    // Set up performance Marker and mark bootstrap
    const performanceMarker = new WebPlatformPerformanceMarker();
    performanceMarker.mark(MarkerTag.AppBootstrap);
    const hostAdaptor = new MetaOSHostAdaptor(performanceMarker, LoggingStrings.ProductNameV2);
    const { loggers, configProvider, context, initialRoute, initialRouteFromDeepLink, loader, persistentStorage, telemetryLoggers, user } = await configureHostAdaptor(hostAdaptor, performanceMarker);
    // Determine engagement telemetry constant based on client type
    const clientType = configProvider().sessionMetaData.appMetadata.clientType;
    const engagementTelemetryConstant = clientType === ClientType.Office ? EngagementTelemetryConstants.OfficeApp : EngagementTelemetryConstants.MetaOSHost;
    const { agentProviders, authModule, dispatcher, requestPrioritizer, serviceProviders, storeLoader } = setupProviders(configProvider, context, engagementTelemetryConstant, initialRoute, initialRouteFromDeepLink, hostAdaptor, loader, loggers, user, persistentStorage);
    // CAE probe: Use the Graph Planner service to trigger CAE enforcement.
    // Planner v4 service hasn't implemented CAE, but Graph has. Start probe now, await before render.
    const caeProbePromise = triggerCaeGraphProbeAsync(configProvider, loggers, serviceProviders.plannerGraph);
    // Create logic module loader with the authModule instance from setupProviders
    // This ensures a single AuthModule instance is used across the app for consistent
    // token management, expiry checks, and caching
    const lmLoader = new MetaOSAppLogicModuleLoader(loader, dispatcher, loggers, serviceProviders, storeLoader, hostAdaptor, configProvider, authModule);
    const acLoader = new BaseActionCreatorLoader(configProvider, dispatcher, loader, lmLoader, loggers, storeLoader);
    // Trigger the data pre-fetch
    if (!configProvider().settings.disablePreFetcher) {
        initDataPreFetcher(acLoader.load(ActionCreator.DataPreFetch), initialRouteFromDeepLink, context, hostAdaptor, loggers, configProvider);
    }
    const { workerLoader, showDevTools } = setupWorkerLoaderAndRegisterTheme(acLoader, configProvider, context, dispatcher, hostAdaptor, loggers, requestPrioritizer, storeLoader, serviceProviders, loader, lmLoader, telemetryLoggers, authModule, persistentStorage);
    // Wait for CAE probe to complete before rendering. The Graph Planner service's
    // TokenRefreshAndRetryAjaxClient already handles CAE flows (claims challenge → token refresh → retry).
    await caeProbePromise;
    const rootElement = document.getElementById(AppRootElementId);
    if (!rootElement) {
        loggers.traceLogger.logTrace(0x1e0d51d2 /* tag_4dvhs */, TraceLevel.Error, `Element with id ${AppRootElementId} not found in the DOM.`);
        return;
    }
    const root = createRoot(rootElement);
    root.render(React.createElement(PlannerShell, { authModule: authModule, dispatcher: dispatcher, serviceProviders: serviceProviders, workerLoader: workerLoader, hostAdaptor: hostAdaptor, loggers: loggers, onLoad: () => hostAdaptor.notifyLoaded(), onSuccess: () => {
            if (!configProvider().flights.EnableTeamsNotifySuccessV2) {
                // common for all meta OS hubs.
                hostAdaptor.notifyRenderSuccess();
            }
        }, onFail: (message) => hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message }), configProvider: configProvider, showDevTools: showDevTools, agentProviders: agentProviders, acLoader: acLoader, storeLoader: storeLoader, isDevMode: false }));
}
